/**
 * Backup telemetry (F19 client).
 *
 * Per-mechanism counters for the four backup paths:
 *   per_user           — `message_backups` (per-recipient, AES-256-GCM under backup key)
 *   channel_shared     — `channel_recovery_backups` (per channel, shared recovery key)
 *   bot                — bot message backup row (server-side write, decrypted with well-known key)
 *   peer_sender_key    — `peer_sender_key_backups` (Option E, per-user backup key)
 *
 * Each mechanism tracks:
 *   writes          — successful upload from this client
 *   reads           — fetched entries from server
 *   decryptSuccess  — local decrypt of a fetched entry succeeded
 *   decryptFail     — local decrypt of a fetched entry failed
 *
 * Counters are flushed every 5 min to POST /debug/backup-stats. The
 * server merges them into the same in-process aggregator that holds the
 * server-side write counts (see backup-telemetry.service.ts).
 *
 * Reset on successful flush; on failure the in-memory counters are
 * preserved and re-flushed on the next interval.
 *
 * Telemetry is ALWAYS on — purely numeric counters, no plaintext leak.
 * Fire-and-forget: any error must never bubble into the decrypt path.
 */
import { api } from "./api";

type Mechanism =
  | "per_user"
  | "channel_shared"
  | "bot"
  | "peer_sender_key"
  | "per_user_plaintext_fallback"
  | "peer_plaintext_failsafe";

interface Counter {
  writes: number;
  reads: number;
  decryptSuccess: number;
  decryptFail: number;
}

const FLUSH_INTERVAL_MS = 5 * 60 * 1000;

function emptyCounter(): Counter {
  return { writes: 0, reads: 0, decryptSuccess: 0, decryptFail: 0 };
}

const counters: Record<Mechanism, Counter> = {
  per_user: emptyCounter(),
  channel_shared: emptyCounter(),
  bot: emptyCounter(),
  peer_sender_key: emptyCounter(),
  per_user_plaintext_fallback: emptyCounter(),
  peer_plaintext_failsafe: emptyCounter(),
};

let flushTimer: ReturnType<typeof setInterval> | null = null;
let lifecycleBound = false;

export function recordBackupWrite(m: Mechanism): void {
  counters[m].writes++;
}

export function recordBackupRead(m: Mechanism, count = 1): void {
  counters[m].reads += count;
}

const MAX_REASON_LEN = 64;
const MAX_REASONS_PER_MECH = 32;

let failReasons: Partial<Record<Mechanism, Record<string, number>>> = {};

function normalizeReason(reason: string): string {
  // Defense: bound length + strip newlines so log payload stays single-line.
  return reason.replace(/\s+/g, "_").slice(0, MAX_REASON_LEN);
}

export function recordBackupDecryptOutcome(
  m: Mechanism,
  success: boolean,
  reason?: string,
): void {
  if (success) {
    counters[m].decryptSuccess++;
    return;
  }
  counters[m].decryptFail++;
  if (!reason) return;
  const key = normalizeReason(reason);
  const bucket = failReasons[m] ?? {};
  // Cap distinct reasons per mechanism to avoid unbounded growth from unique error strings.
  if (!(key in bucket) && Object.keys(bucket).length >= MAX_REASONS_PER_MECH) return;
  bucket[key] = (bucket[key] ?? 0) + 1;
  failReasons[m] = bucket;
}

function isAllZero(): boolean {
  for (const m of Object.keys(counters) as Mechanism[]) {
    const c = counters[m];
    if (c.writes !== 0 || c.reads !== 0 || c.decryptSuccess !== 0 || c.decryptFail !== 0) {
      return false;
    }
  }
  return true;
}

function snapshot(): Record<Mechanism, Counter> {
  return {
    per_user: { ...counters.per_user },
    channel_shared: { ...counters.channel_shared },
    bot: { ...counters.bot },
    peer_sender_key: { ...counters.peer_sender_key },
    per_user_plaintext_fallback: { ...counters.per_user_plaintext_fallback },
    peer_plaintext_failsafe: { ...counters.peer_plaintext_failsafe },
  };
}

function snapshotFailReasons(): Partial<Record<Mechanism, Record<string, number>>> {
  const out: Partial<Record<Mechanism, Record<string, number>>> = {};
  for (const [m, bucket] of Object.entries(failReasons) as [Mechanism, Record<string, number>][]) {
    out[m] = { ...bucket };
  }
  return out;
}

function resetCounters(): void {
  counters.per_user = emptyCounter();
  counters.channel_shared = emptyCounter();
  counters.bot = emptyCounter();
  counters.peer_sender_key = emptyCounter();
  counters.per_user_plaintext_fallback = emptyCounter();
  counters.peer_plaintext_failsafe = emptyCounter();
  failReasons = {};
}

async function flush(): Promise<void> {
  if (isAllZero()) return;
  const snap = snapshot();
  const reasons = snapshotFailReasons();
  try {
    // No { raw: true }: api.post throws on non-2xx so the catch below preserves
    // counters on server errors, retrying on the next interval as intended.
    await api.post("/debug/backup-stats", { counters: snap, failReasons: reasons });
    // Reset only after confirmed success.
    resetCounters();
  } catch (err) {
    // Swallow: telemetry must never break the app. Keep counters; retry next interval.
    console.warn("[backup-telemetry] flush failed, will retry:", err);
  }
}

export function startBackupTelemetry(): void {
  if (flushTimer) return;
  flushTimer = setInterval(() => { void flush(); }, FLUSH_INTERVAL_MS);
  if (!lifecycleBound && typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") void flush();
    });
    window.addEventListener("pagehide", () => { void flush(); });
    lifecycleBound = true;
  }
}

export function stopBackupTelemetry(): void {
  if (flushTimer) { clearInterval(flushTimer); flushTimer = null; }
}
